import { Link } from 'react-router-dom';

export interface BreadcrumbItem {
  label: string;
  /**
   * Where the crumb goes. PREFER THIS over `onClick`: it renders a real anchor,
   * so cmd-click, middle-click, "copy link address" and the browser's own
   * hover-preview all work, and assistive tech announces a link rather than a
   * button. A trail built from `onClick` handlers looks identical and does none
   * of it.
   */
  to?: string;
  /**
   * For crumbs that move state the URL does not fully describe. Deliver's trail
   * is the live case: its nav state syncs one way, state -> URL, and the URL is
   * only read at mount — so a link would change the address bar and leave the
   * screen where it was. Given both, `to` wins.
   */
  onClick?: () => void;
  isCurrent?: boolean;
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
  /**
   * Slot pinned to the right end of the breadcrumb row — for small, always-there
   * page-scoped affordances (the models widget) that would otherwise fight the
   * action buttons for space in the header. Keep it to chips, not controls.
   */
  right?: React.ReactNode;
  /**
   * Spacing override, for hosts that own their own rhythm. The default margin
   * suits a page that OPENS with the trail; a compact header bar with a title
   * directly underneath wants it tighter.
   */
  className?: string;
}

/**
 * The one breadcrumb.
 *
 * There were three: this one, a `›`-separated copy inside the subject
 * workspace, and a third inside SettingsLayout with its own incompatible item
 * type (`to` where this one had `onClick`). Same idea, three sizes, two
 * separators, two ways of being clicked — so which trail you got, and whether
 * you could cmd-click it, depended on which screen you were standing on.
 *
 * Usage:
 * <Breadcrumb items={[
 *   { label: 'Subjects', to: '/subjects' },
 *   { label: 'My Subject', to: '/subjects/1' },
 *   { label: 'Current Page', isCurrent: true },
 * ]} right={<StageModelsWidget surface="page_builder" />} />
 */
export default function Breadcrumb({
  items,
  right,
  className = 'mb-4 sm:mb-6',
}: BreadcrumbProps) {
  return (
    <nav
      aria-label="Breadcrumb"
      className={`flex items-center justify-between gap-3 ${className}`}
    >
      <ol className="flex items-center flex-wrap gap-1 sm:gap-2 text-sm">
        {items.map((item, index) => {
          // The LAST crumb is the current page, whatever it was passed. A
          // middle crumb with no way to click it is a gap in the trail, not a
          // second "you are here" — treating it as current was how a
          // half-built trail rendered two bold segments and looked deliberate.
          const isLast = index === items.length - 1;
          const current = item.isCurrent ?? isLast;
          const clickable = !current && (item.to || item.onClick);

          return (
            <li key={index} className="flex items-center gap-1 sm:gap-2">
              {index > 0 && <span className="text-gray-400 dark:text-gray-500">/</span>}
              {!clickable ? (
                <span
                  aria-current={current ? 'page' : undefined}
                  className={
                    current
                      ? 'font-semibold text-gray-900 dark:text-gray-100'
                      : 'text-gray-500 dark:text-gray-400'
                  }
                >
                  {item.label}
                </span>
              ) : item.to ? (
                <Link
                  to={item.to}
                  className="text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  onClick={item.onClick}
                  className="text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                >
                  {item.label}
                </button>
              )}
            </li>
          );
        })}
      </ol>
      {right && <div className="flex-shrink-0">{right}</div>}
    </nav>
  );
}
